import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useIntl } from '@grandvisionhq/state';
import { createScriptTag, injectTags } from '@grandvisionhq/utils';
import reducer, { initialState } from './reducer.js';
import { createPowerReviewsConfig, mergeConfigs } from '../utils/create-powerreviews-config.js';

// These are the keys PowerReviews requires in the `components` object when initializing a widget
const mapStateToPowerReviewsType = {
    reviewWrite: 'Write',
    reviewSnippet: 'ReviewSnippet',
    reviewDisplay: 'ReviewDisplay'
};
const ReviewsContext = /*#__PURE__*/ React.createContext({});
const useReviewsContext = ()=>React.useContext(ReviewsContext)
;
const PR_SCRIPT_ID = 'power-reviews-script';
const PR_SCRIPT_SRC = 'https://ui.powerreviews.com/stable/4.1/ui.js';
const ReviewsProvider = ({ enabled , config , children ,  })=>{
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const { locale  } = useIntl();
    const widgets = React.useMemo(()=>{
        return Object.keys(state).reduce((result, key)=>{
            if (state[key] !== null) {
                const widgetConfig = createPowerReviewsConfig(swcHelpers.objectSpread({
                    config,
                    locale
                }, state[key], {
                    type: mapStateToPowerReviewsType[key]
                }));
                return [
                    ...result,
                    widgetConfig
                ];
            }
            return result;
        }, []);
    }, [
        state
    ]);
    React.useEffect(()=>{
        const script = createScriptTag({
            id: PR_SCRIPT_ID,
            src: PR_SCRIPT_SRC,
            defer: true
        });
        injectTags([
            script
        ]);
    }, []);
    React.useEffect(()=>{
        if (enabled && widgets.length) {
            const mergedConfig = widgets.length > 1 ? mergeConfigs(widgets) : widgets[0];
            // Clear existing widgets and loaders before (re)initializing
            Object.values(mergedConfig.components).forEach((widgetId)=>{
                clearWidgetHtml(widgetId);
            });
            // Set powerreviews configuration. powerreviews has a complex async script loading and does not provide a proper load hook.
            // To prevent a race condition we prepare the config beforehand.
            // This is the official way powerreviews should be declared.
            window.pwr = window.pwr || function(...args) {
                (pwr.q = pwr.q || []).push(args);
            };
            window.pwr('render', mergedConfig);
            // When new widgets are updated, first remove the old from powerreviews as they might have the same id and therefore will not be rendered.
            return ()=>{
                Object.values(mergedConfig.components).forEach((widgetId)=>{
                    window.pwr('unmount', widgetId);
                });
            };
        }
    }, [
        widgets,
        enabled
    ]);
    const value = React.useMemo(()=>({
            enabled,
            config,
            dispatch
        })
    , [
        enabled,
        config
    ]);
    return /*#__PURE__*/ React.createElement(ReviewsContext.Provider, {
        value: value
    }, children);
};
// Because PowerReviews doesn't clear their widget's HTML themselves
// when we re-call their `display` method
function clearWidgetHtml(id) {
    const element = document.getElementById(id);
    if (element) {
        element.innerHTML = '';
    }
}

export { PR_SCRIPT_ID, PR_SCRIPT_SRC, ReviewsProvider, ReviewsContext as default, mapStateToPowerReviewsType, useReviewsContext };
//# sourceMappingURL=ReviewsContext.js.map
